import React, { useState, useEffect, useRef } from 'react';
import { settingsAPI, uploadAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Waves, Mail, Phone, MapPin, Loader2, Plus, X, Upload, Edit2, Trash2, Image } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const About = () => {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editImageUrl, setEditImageUrl] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (newSettings) => {
    setSaving(true);
    try {
      await settingsAPI.update(newSettings);
      setSettings(newSettings);
      toast.success('Gallery updated successfully');
    } catch (error) {
      toast.error('Failed to update gallery');
    } finally {
      setSaving(false);
    }
  };

  const handleAddImageUrl = () => {
    if (newImageUrl && newImageUrl.trim()) {
      const updatedSettings = {
        ...settings,
        gallery_images: [...(settings.gallery_images || []), newImageUrl.trim()]
      };
      saveSettings(updatedSettings);
      setNewImageUrl('');
      setGalleryDialogOpen(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const response = await uploadAPI.uploadPhoto(file, 'gallery', `gallery-${Date.now()}`);
      const imageUrl = response.data.photo_url.startsWith('http') 
        ? response.data.photo_url 
        : `${BACKEND_URL}${response.data.photo_url}`;
      
      const updatedSettings = {
        ...settings,
        gallery_images: [...(settings.gallery_images || []), imageUrl]
      };
      await saveSettings(updatedSettings);
      setGalleryDialogOpen(false);
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteImage = (index) => {
    const updatedImages = [...(settings.gallery_images || [])];
    updatedImages.splice(index, 1);
    const updatedSettings = { ...settings, gallery_images: updatedImages };
    saveSettings(updatedSettings);
  };

  const handleEditImage = (index) => {
    setEditingIndex(index);
    setEditImageUrl(settings.gallery_images[index]);
  };

  const handleSaveEditImage = () => {
    if (editImageUrl && editImageUrl.trim() && editingIndex !== null) {
      const updatedImages = [...(settings.gallery_images || [])];
      updatedImages[editingIndex] = editImageUrl.trim();
      const updatedSettings = { ...settings, gallery_images: updatedImages };
      saveSettings(updatedSettings);
      setEditingIndex(null);
      setEditImageUrl('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Hero Section */}
      <div className="relative rounded-3xl overflow-hidden" data-testid="about-hero">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1730244548329-4ae2f4fcaa7c?w=1920')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-primary-900/95" />
        <div className="relative z-10 px-8 py-16 text-center text-white">
          <div className="flex justify-center mb-6">
            {settings?.logo_url ? (
              <img 
                src={settings.logo_url} 
                alt="Academy Logo" 
                className="h-24 w-24 object-contain rounded-2xl bg-white/10 p-2"
              />
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Waves className="w-12 h-12" />
              </div>
            )}
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            {settings?.academy_name || 'Eswatini Seals Swim Academy'}
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Developing champions one stroke at a time
          </p>
        </div>
      </div>

      {/* About Section */}
      <Card>
        <CardContent className="p-8">
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">About Us</h2>
          <div className="prose prose-slate max-w-none">
            {settings?.about_text ? (
              <p className="text-slate-600 leading-relaxed whitespace-pre-line">{settings.about_text}</p>
            ) : (
              <p className="text-slate-600 leading-relaxed">
                Welcome to Eswatini Seals Swim Academy! We are dedicated to developing confident, skilled swimmers 
                through professional instruction and a passion for aquatic excellence. Our experienced coaches 
                provide personalized attention to students of all ages and skill levels, from beginners taking 
                their first strokes to competitive swimmers aiming for excellence.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Gallery Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl font-bold text-slate-900">Gallery</h2>
          {isAdmin() && (
            <Button 
              onClick={() => setGalleryDialogOpen(true)} 
              className="btn-primary"
              data-testid="add-gallery-image-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Image
            </Button>
          )}
        </div>
        
        {settings?.gallery_images && settings.gallery_images.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {settings.gallery_images.map((url, index) => (
              <div key={index} className="relative aspect-video rounded-xl overflow-hidden group" data-testid={`gallery-image-${index}`}>
                <img 
                  src={url} 
                  alt={`Gallery ${index + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
                {isAdmin() && (
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleEditImage(index)}
                      data-testid={`edit-gallery-${index}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteImage(index)}
                      data-testid={`delete-gallery-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-slate-50 rounded-xl">
            <Image className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-400">No gallery images yet</p>
            {isAdmin() && (
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setGalleryDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Image
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Add Gallery Image Dialog */}
      <Dialog open={galleryDialogOpen} onOpenChange={setGalleryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Gallery Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="gallery-file-input"
            />
            
            <div 
              className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              {uploadingImage ? (
                <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500">Click to upload an image</p>
                  <p className="text-xs text-slate-400 mt-1">JPEG, PNG, WebP, GIF (max 5MB)</p>
                </>
              )}
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-400">Or enter URL</span>
              </div>
            </div>

            <div className="flex gap-2">
              <Input
                value={newImageUrl}
                onChange={(e) => setNewImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                data-testid="gallery-url-input"
              />
              <Button onClick={handleAddImageUrl} disabled={!newImageUrl.trim()}>
                Add
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Gallery Image Dialog */}
      <Dialog open={editingIndex !== null} onOpenChange={(open) => !open && setEditingIndex(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Gallery Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {editingIndex !== null && settings?.gallery_images?.[editingIndex] && (
              <div className="aspect-video rounded-lg overflow-hidden bg-slate-100">
                <img 
                  src={editImageUrl || settings.gallery_images[editingIndex]} 
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <Input
              value={editImageUrl}
              onChange={(e) => setEditImageUrl(e.target.value)}
              placeholder="Enter new image URL"
              data-testid="edit-gallery-url-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingIndex(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEditImage} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Section */}
      <Card>
        <CardContent className="p-8">
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">Contact Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {settings?.contact_email && (
              <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <a href={`mailto:${settings.contact_email}`} className="text-slate-900 font-medium hover:text-primary">
                    {settings.contact_email}
                  </a>
                </div>
              </div>
            )}
            
            {settings?.contact_phone && (
              <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50">
                <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center text-secondary">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <a href={`tel:${settings.contact_phone}`} className="text-slate-900 font-medium hover:text-primary">
                    {settings.contact_phone}
                  </a>
                </div>
              </div>
            )}
            
            {settings?.address && (
              <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50">
                <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center text-accent">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Address</p>
                  <p className="text-slate-900 font-medium">{settings.address}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default About;
